import {
  Button, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuItemRadio,
  MenuGroup, MenuGroupHeader, MenuDivider, type MenuProps,
} from '@fluentui/react-components';
import { ChevronDownRegular } from '@fluentui/react-icons';
import { GROUP_GRANULARITIES, type GroupGranularity } from './groupBy';

// The fleet's column header menu (fleet filter contract v3 Part II §13.1, rule M1). Offers EXACTLY
// the items the column/table supports (C58: omitted, never shown disabled) -- a strict ADDITION
// next to the existing sortable-header click-to-cycle behavior and the existing filter funnel
// (ColumnHeaderFilter), not a replacement of either: a plain click on the header still cycles sort
// exactly as before (zero regression to any existing test), and the filter funnel is unchanged.
// Column WIDTH and MOVE LEFT/RIGHT are the other two M1 items -- DataTable supports neither today,
// so they are correctly OMITTED here (C58), not a gap.
//
// Group by is itself optional-conformance (M3): only columns the caller marks groupable render the
// "Group by" section at all; a column with none renders the sort-only menu, matching C66 ("a table
// that does not support grouping omits Group by and is conformant").

export interface ColumnMenuGroupProps {
  /** True when THIS column is the table's currently-active group column. */
  active: boolean;
  /** The active granularity, only meaningful when `active` is true. */
  granularity: GroupGranularity | null;
  onSetGroup: (granularity: GroupGranularity) => void;
  onClearGroup: () => void;
}

export interface ColumnMenuLabels {
  sortAZ: string;
  sortZA: string;
  groupByHeader: string;
  clearGrouping: string;
  granularity: Record<GroupGranularity, string>;
  /** Accessible name for the trigger button, e.g. "Status column menu". */
  triggerLabel: string;
}

const DEFAULT_LABELS: ColumnMenuLabels = {
  sortAZ: 'Sort A → Z',
  sortZA: 'Sort Z → A',
  groupByHeader: 'Group by',
  clearGrouping: 'Clear grouping',
  granularity: { year: 'Year', quarter: 'Quarter', month: 'Month', week: 'Week', day: 'Day' },
  triggerLabel: 'column menu',
};

export interface ColumnMenuProps {
  columnLabel: string;
  onSort: (direction: 'asc' | 'desc') => void;
  /** Omitted entirely (no "Group by" section at all) when the column doesn't support grouping. */
  group?: ColumnMenuGroupProps;
  labels?: Partial<ColumnMenuLabels>;
}

export function ColumnMenu({ columnLabel, onSort, group, labels }: ColumnMenuProps) {
  const L = { ...DEFAULT_LABELS, ...labels, granularity: { ...DEFAULT_LABELS.granularity, ...labels?.granularity } };

  const onCheckedValueChange: MenuProps['onCheckedValueChange'] = (_e, data) => {
    const next = data.checkedItems[0];
    if (next) group?.onSetGroup(next as GroupGranularity);
  };

  return (
    <Menu
      // Always a DEFINED checkedValues once `group` is passed at all (even an empty selection),
      // never undefined -- Fluent's useControllableState warns ("changing an uncontrolled value to
      // be controlled") if this flips from undefined to a real object the first time a column is
      // actually grouped, which it did here before this fix (observed under test).
      checkedValues={group ? { granularity: group.active && group.granularity ? [group.granularity] : [] } : undefined}
      onCheckedValueChange={group ? onCheckedValueChange : undefined}
    >
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="transparent"
          size="small"
          icon={<ChevronDownRegular />}
          aria-label={`${columnLabel} ${L.triggerLabel}`}
          onClick={(e) => e.stopPropagation()}
        />
      </MenuTrigger>
      <MenuPopover onClick={(e) => e.stopPropagation()}>
        <MenuList>
          <MenuItem onClick={() => onSort('asc')}>{L.sortAZ}</MenuItem>
          <MenuItem onClick={() => onSort('desc')}>{L.sortZA}</MenuItem>
          {group ? (
            <>
              <MenuDivider />
              <MenuGroup>
                <MenuGroupHeader>{L.groupByHeader}</MenuGroupHeader>
                {GROUP_GRANULARITIES.map((g) => (
                  <MenuItemRadio key={g} name="granularity" value={g}>
                    {L.granularity[g]}
                  </MenuItemRadio>
                ))}
              </MenuGroup>
              {group.active ? (
                <MenuItem onClick={group.onClearGroup}>{L.clearGrouping}</MenuItem>
              ) : null}
            </>
          ) : null}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

export default ColumnMenu;
